import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { buildClubHref, fetchCatalog, resolveClubReference } from '@/features/catalog/catalog.api';
import { buildLegalHref } from '@/features/legal/legal-navigation';
import { trackEvent } from '@/shared/lib/analytics';
import { trackYandexGoal } from '@/shared/lib/yandex-metrica';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { NeutralLoadingState } from '@/shared/components/ui/NeutralLoadingState';
import { NeutralSearchControl } from '@/shared/components/ui/NeutralSearchControl';
import { NeutralStatePanel } from '@/shared/components/ui/NeutralStatePanel';
import type { CatalogClubItem, ClubHostPublic, ClubStatus } from '@bookclub/shared';

function useDebouncedSearch(value: string, delay = 400): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

type CatalogPreviewState = 'default' | 'search' | 'single' | 'empty' | 'error' | 'loading';
type CatalogStatusFilter = ClubStatus | null;

const CATALOG_STATUS_FILTERS: ReadonlyArray<{
  label: string;
  value: CatalogStatusFilter;
}> = [
  { label: 'Все клубы', value: null },
  { label: 'Идёт сейчас', value: 'live' },
  { label: 'Скоро', value: 'upcoming' },
  { label: 'В записи', value: 'recorded' },
];

const PREVIEW_STATE_VALUES: CatalogPreviewState[] = [
  'default',
  'search',
  'single',
  'empty',
  'error',
  'loading',
];

const PREVIEW_QUERY_BY_STATE: Partial<Record<CatalogPreviewState, string>> = {
  search: 'толстой',
};

const PREVIEW_AUTHOR_AVATAR_URL =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 64 64%22%3E%3Crect width=%2264%22 height=%2264%22 rx=%2232%22 fill=%22%23efe5d2%22/%3E%3Ccircle cx=%2232%22 cy=%2226%22 r=%2212%22 fill=%22%23826b4d%22/%3E%3Cpath d=%22M16 54c3-10 11-16 16-16s13 6 16 16%22 fill=%22%23826b4d%22/%3E%3C/svg%3E';
const PREVIEW_SECOND_AUTHOR_AVATAR_URL =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 64 64%22%3E%3Crect width=%2264%22 height=%2264%22 rx=%2232%22 fill=%22%23efe6db%22/%3E%3Ccircle cx=%2232%22 cy=%2226%22 r=%2212%22 fill=%22%235a4634%22/%3E%3Cpath d=%22M16 54c3-10 11-16 16-16s13 6 16 16%22 fill=%22%235a4634%22/%3E%3C/svg%3E';

const BASELINE_PREVIEW_ITEMS: CatalogClubItem[] = [
  {
    id: 'club-anna-karenina',
    paywallSlug: null,
    title: 'Анна Каренина',
    status: 'live',
    isUnpublished: false,
    bookAuthor: 'Лев Толстой',
    host: {
      firstName: 'Лев',
      lastName: 'Толстой',
      fullName: 'Лев Толстой',
      shortName: 'Л. Толстой',
      description: null,
      subtitle: 'Русский писатель',
      avatarUrl: PREVIEW_AUTHOR_AVATAR_URL,
    },
    secondHost: null,
    author: 'Лев Толстой',
    authorSubtitle: 'Русский писатель',
    authorAvatarUrl: PREVIEW_AUTHOR_AVATAR_URL,
    secondAuthor: null,
    secondAuthorSubtitle: null,
    secondAuthorAvatarUrl: null,
    coverUrl: null,
    accessStatus: 'owned',
  },
  {
    id: 'club-war-and-peace',
    paywallSlug: null,
    title: 'Война и мир',
    status: 'upcoming',
    isUnpublished: false,
    bookAuthor: 'Лев Толстой',
    host: {
      firstName: 'Лев',
      lastName: 'Толстой',
      fullName: 'Лев Толстой',
      shortName: 'Л. Толстой',
      description: null,
      subtitle: null,
      avatarUrl: null,
    },
    secondHost: {
      firstName: 'Софья',
      lastName: 'Толстая',
      fullName: 'Софья Толстая',
      shortName: 'С. Толстая',
      description: null,
      subtitle: 'Со-ведущая',
      avatarUrl: PREVIEW_SECOND_AUTHOR_AVATAR_URL,
    },
    author: 'Лев Толстой',
    authorSubtitle: null,
    authorAvatarUrl: null,
    secondAuthor: 'Софья Толстая',
    secondAuthorSubtitle: 'Со-ведущая',
    secondAuthorAvatarUrl: PREVIEW_SECOND_AUTHOR_AVATAR_URL,
    coverUrl: null,
    accessStatus: 'locked',
  },
  {
    id: 'club-brothers-karamazov',
    paywallSlug: null,
    title: 'Братья Карамазовы',
    status: 'recorded',
    isUnpublished: false,
    bookAuthor: 'Фёдор Достоевский',
    host: {
      firstName: 'Фёдор',
      lastName: 'Достоевский',
      fullName: 'Фёдор Достоевский',
      shortName: 'Ф. Достоевский',
      description: null,
      subtitle: null,
      avatarUrl: null,
    },
    secondHost: null,
    author: 'Фёдор Достоевский',
    authorSubtitle: null,
    authorAvatarUrl: null,
    secondAuthor: null,
    secondAuthorSubtitle: null,
    secondAuthorAvatarUrl: null,
    coverUrl: null,
    accessStatus: 'locked',
  },
];

const SINGLE_PREVIEW_ITEMS: CatalogClubItem[] = [BASELINE_PREVIEW_ITEMS[0]];
const COVER_TONES = [
  {
    background: 'linear-gradient(160deg, rgba(226,220,204,0.92) 0%, rgba(248,244,235,0.98) 100%)',
    accent: 'rgba(112, 97, 76, 0.78)',
  },
  {
    background: 'linear-gradient(160deg, rgba(217,228,224,0.92) 0%, rgba(244,248,246,0.98) 100%)',
    accent: 'rgba(77, 103, 96, 0.78)',
  },
  {
    background: 'linear-gradient(160deg, rgba(230,220,212,0.92) 0%, rgba(248,244,240,0.98) 100%)',
    accent: 'rgba(112, 85, 70, 0.78)',
  },
];

function normalizeText(value: string) {
  return value.trim().toLocaleLowerCase('ru-RU');
}

function filterCatalogItems(items: CatalogClubItem[], query: string, status: CatalogStatusFilter) {
  const statusFilteredItems = status ? items.filter((club) => club.status === status) : items;
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return statusFilteredItems;

  return statusFilteredItems.filter((club) => {
    const haystack = [
      club.title,
      club.bookAuthor ?? '',
      club.host.fullName ?? '',
      club.host.shortName ?? '',
      club.secondHost?.fullName ?? '',
      club.secondHost?.shortName ?? '',
    ]
      .map(normalizeText)
      .join(' ');
    return haystack.includes(normalizedQuery);
  });
}

function getCoverTone(seed: string) {
  const sum = Array.from(seed).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return COVER_TONES[sum % COVER_TONES.length];
}

function getCoverMonogram(title: string) {
  const parts = title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '');

  return parts.join('') || 'К';
}

function getPreviewState(search: string): CatalogPreviewState | null {
  if (!(import.meta.env.DEV || import.meta.env.MODE === 'test')) {
    return null;
  }

  const rawState = new URLSearchParams(search).get('catalogState');
  if (!rawState) return null;

  return PREVIEW_STATE_VALUES.includes(rawState as CatalogPreviewState)
    ? (rawState as CatalogPreviewState)
    : null;
}

function getPreviewItems(previewState: CatalogPreviewState | null) {
  switch (previewState) {
    case 'single':
      return SINGLE_PREVIEW_ITEMS;
    case 'empty':
    case 'error':
    case 'loading':
      return [];
    case 'search':
    case 'default':
    default:
      return BASELINE_PREVIEW_ITEMS;
  }
}

function getAuthorMonogram(name?: string | null) {
  if (!name) return 'IN';

  const parts = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '');

  return parts.join('') || 'IN';
}

type CatalogHostViewModel = {
  fullName: string;
  shortName: string;
  subtitle: string | null;
  avatarUrl: string | null;
};

function toCatalogHostViewModel(host: ClubHostPublic | null | undefined): CatalogHostViewModel | null {
  const fullName = host?.fullName?.trim() ?? '';
  const shortName = host?.shortName?.trim() ?? '';
  const resolvedName = shortName || fullName;

  if (!resolvedName) {
    return null;
  }

  return {
    fullName,
    shortName: resolvedName,
    subtitle: host?.subtitle ?? null,
    avatarUrl: host?.avatarUrl ?? null,
  };
}

function getCatalogHosts(club: CatalogClubItem) {
  return [toCatalogHostViewModel(club.host), toCatalogHostViewModel(club.secondHost)].filter(
    (host): host is CatalogHostViewModel => Boolean(host),
  );
}

function CatalogPill({
  label,
  active = false,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        'inline-flex h-9 shrink-0 items-center rounded-full border px-4 text-[13px] font-medium tracking-[0.005em] transition-colors min-[430px]:h-10',
        active
          ? 'border-[rgba(107,78,230,0.22)] bg-[var(--primary)] text-[var(--primary-foreground)] shadow-[0_10px_20px_rgba(107,78,230,0.18)]'
          : 'border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] text-[rgba(26,26,26,0.58)]',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function CatalogCard({
  club,
  onOpen,
}: {
  club: CatalogClubItem;
  onOpen: (club: CatalogClubItem) => void;
}) {
  const tone = getCoverTone(club.id);
  const monogram = getCoverMonogram(club.title);
  const hosts = getCatalogHosts(club);
  const hasSecondHost = hosts.length > 1;
  const bookAuthor = club.bookAuthor?.trim() ?? '';
  const isOpenable = Boolean(resolveClubReference(club));
  const cardClassName = [
    'group flex h-full w-full flex-col overflow-hidden rounded-[22px] border border-[rgba(26,26,26,0.08)] bg-[rgba(255,248,231,0.9)] text-left shadow-[0_10px_24px_rgba(62,48,23,0.08)]',
    isOpenable ? 'transition-transform active:scale-[0.985]' : '',
  ].join(' ');

  const cardContent = (
    <>
      <div className="relative h-[124px] overflow-hidden rounded-[22px] rounded-b-none min-[430px]:h-[138px] min-[768px]:h-[150px]">
        {club.coverUrl ? (
          <>
            <img src={club.coverUrl} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,248,231,0.08),rgba(26,26,26,0.18))]" />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center" style={{ background: tone.background }}>
            <span
              className="text-[24px] font-black tracking-[0.06em]"
              style={{ color: tone.accent }}
              aria-hidden="true"
            >
              {monogram}
            </span>
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-3.5 min-[430px]:gap-3.5 min-[430px]:p-4 min-[768px]:gap-4">
        {hasSecondHost ? (
          <div className="flex items-center gap-2.5" data-testid={`catalog-card-hosts-${club.id}`}>
            <div className="relative h-8 w-[52px] shrink-0" aria-hidden="true">
              {hosts.slice(0, 2).map((host, index) => (
                <div
                  key={`${club.id}-${host.fullName || host.shortName}`}
                  className={[
                    'absolute top-0 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-[rgba(255,255,255,0.92)] bg-[rgba(255,255,255,0.78)] text-[10px] font-semibold tracking-[0.08em] text-[rgba(26,26,26,0.58)] shadow-[0_4px_12px_rgba(62,48,23,0.08)]',
                    index === 0 ? 'left-0 z-10' : 'left-5 z-20',
                  ].join(' ')}
                >
                  {host.avatarUrl ? (
                    <img src={host.avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span>{getAuthorMonogram(host.fullName || host.shortName)}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="min-w-0">
              {hosts.slice(0, 2).map((host) => (
                <p
                  key={`${club.id}-${host.fullName || host.shortName}-label`}
                  className="truncate text-[11px] font-medium leading-[1.1] text-[rgba(26,26,26,0.72)]"
                >
                  {host.shortName}
                </p>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.78)] text-[10px] font-semibold tracking-[0.08em] text-[rgba(26,26,26,0.58)]"
              aria-hidden="true"
            >
              {hosts[0]?.avatarUrl ? (
                <img
                  src={hosts[0].avatarUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{getAuthorMonogram(hosts[0]?.fullName || hosts[0]?.shortName)}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-[11px] font-medium leading-none text-[rgba(26,26,26,0.58)]">
                {hosts[0]?.shortName ?? 'Клуб'}
              </p>
              {hosts[0]?.subtitle ? (
                <p className="truncate text-[10px] leading-none text-[rgba(26,26,26,0.38)]">
                  {hosts[0].subtitle}
                </p>
              ) : null}
            </div>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-3 text-[13px] font-black uppercase leading-[1.02] tracking-[0.012em] text-[var(--foreground)] min-[430px]:text-[15px]">
              {club.title}
            </h3>
            {club.isUnpublished ? (
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-[rgba(26,26,26,0.44)]">
                Не опубликован
              </p>
            ) : null}
          </div>
          {bookAuthor ? (
            <p className="mt-1 truncate text-[11px] font-semibold leading-[1.08] text-[var(--accent)] min-[430px]:text-[11.5px]">
              {bookAuthor}
            </p>
          ) : null}
        </div>

        <div>
          <span
            className={[
              'inline-flex w-full items-center justify-center rounded-full px-4 py-2 text-[12px] font-semibold tracking-[0.01em] transition-colors',
              club.isUnpublished && !isOpenable
                ? 'bg-[rgba(26,26,26,0.08)] text-[rgba(26,26,26,0.48)]'
                : club.accessStatus === 'owned'
                ? 'bg-[var(--foreground)] text-[var(--background)]'
                : 'bg-[var(--primary)] text-[var(--primary-foreground)]',
            ].join(' ')}
          >
            {club.isUnpublished && !isOpenable ? 'Недоступно' : 'Открыть'}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <li className="h-full">
      {isOpenable ? (
        <button
          type="button"
          onClick={() => onOpen(club)}
          aria-label={`Открыть клуб ${club.title}`}
          className={cardClassName}
        >
          {cardContent}
        </button>
      ) : (
        <div aria-label={`Клуб ${club.title} не опубликован`} className={cardClassName}>
          {cardContent}
        </div>
      )}
    </li>
  );
}

export function CatalogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const previewState = useMemo(() => getPreviewState(location.search), [location.search]);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [searchInput, setSearchInput] = useState(
    () => PREVIEW_QUERY_BY_STATE[previewState ?? 'default'] ?? '',
  );
  const [statusFilter, setStatusFilter] = useState<CatalogStatusFilter>(null);
  const debouncedSearch = useDebouncedSearch(searchInput);
  const normalizedSearch = debouncedSearch.trim();

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['catalog', normalizedSearch, statusFilter ?? 'all'],
      queryFn: ({ pageParam }) =>
        fetchCatalog(
          normalizedSearch || undefined,
          typeof pageParam === 'string' ? pageParam : undefined,
          statusFilter ?? undefined,
        ),
      enabled: previewState === null,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: (previousData) => previousData,
      staleTime: 0,
    });

  const previewItems = useMemo(() => getPreviewItems(previewState), [previewState]);
  const liveItems = data?.pages.flatMap((page) => page.items) ?? [];
  const baseItems = previewState === null ? liveItems : previewItems;
  const clubs = useMemo(
    () =>
      previewState === null
        ? baseItems
        : filterCatalogItems(baseItems, normalizedSearch, statusFilter),
    [baseItems, normalizedSearch, previewState, statusFilter],
  );

  const isPreviewLoading = previewState === 'loading';
  const isPreviewError = previewState === 'error';
  const resolvedIsLoading = previewState === null ? isLoading : isPreviewLoading;
  const resolvedError = previewState === null ? error : isPreviewError ? new Error('preview-error') : null;
  const hasSearch = normalizedSearch.length > 0;
  const legalHubHref = buildLegalHref('/legal', location.pathname);

  useEffect(() => {
    trackEvent('catalog_view');
  }, []);

  useEffect(() => {
    if (normalizedSearch) {
      trackEvent('catalog_search', { query: normalizedSearch });
    }
  }, [normalizedSearch]);

  useEffect(() => {
    if (previewState !== null) {
      setSearchInput(PREVIEW_QUERY_BY_STATE[previewState] ?? '');
    }
  }, [previewState]);

  useEffect(() => {
    if (
      previewState !== null ||
      typeof IntersectionObserver === 'undefined' ||
      !hasNextPage ||
      isFetchingNextPage ||
      resolvedIsLoading ||
      resolvedError
    ) {
      return;
    }

    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '240px 0px 240px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    previewState,
    resolvedError,
    resolvedIsLoading,
  ]);

  const handleLegalHubOpen = () => {
    void navigate(legalHubHref);
  };

  return (
    <PageFrame
      topInset="none"
      container="adaptive-app"
      className="space-y-4 pt-[calc(var(--safe-area-top)+0.5rem)] min-[375px]:pt-[calc(var(--safe-area-top)+0.625rem)] min-[430px]:pt-[calc(var(--safe-area-top)+0.75rem)] min-[768px]:space-y-6 min-[768px]:pt-[calc(var(--safe-area-top)+1.25rem)]"
    >
      <header className="space-y-3 min-[768px]:space-y-4">
        <div className="flex items-center gap-3 min-[768px]:gap-5">
          <div className="min-w-0 flex-none pt-1">
            <h1 className="text-[24px] font-black leading-[0.92] tracking-[-0.035em] text-[var(--foreground)] min-[375px]:text-[26px] min-[430px]:text-[28px] min-[768px]:text-[32px]">
              INHOUND
            </h1>
            <p className="sr-only">Каталог клубов</p>
          </div>

          <NeutralSearchControl
            id="catalog-search"
            name="catalog-search"
            ariaLabel="Поиск по каталогу"
            value={searchInput}
            onChange={setSearchInput}
            onClear={() => setSearchInput('')}
            placeholder="Найти клуб..."
            wrapperClassName="min-w-0 flex-1 self-center min-[768px]:max-w-[520px]"
            className="h-12 rounded-[18px] border-[rgba(26,26,26,0.08)] bg-[rgba(255,255,255,0.82)] px-4 pr-14 text-[14px] shadow-[0_10px_22px_rgba(62,48,23,0.05)] placeholder:text-[rgba(26,26,26,0.38)] min-[375px]:h-[50px] min-[768px]:h-12"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] min-[768px]:flex-wrap min-[768px]:overflow-visible min-[768px]:pb-0 [&::-webkit-scrollbar]:hidden">
          {CATALOG_STATUS_FILTERS.map((filter) => (
            <CatalogPill
              key={filter.label}
              label={filter.label}
              active={statusFilter === filter.value}
              onClick={() => setStatusFilter(filter.value)}
            />
          ))}
        </div>
      </header>

      <section aria-label="Список клубов" className="space-y-3 pt-1 min-[768px]:space-y-4">
        {resolvedIsLoading ? <NeutralLoadingState ariaLabel="Загрузка каталога" /> : null}

        {!resolvedIsLoading && resolvedError ? (
          <NeutralStatePanel
            title="Каталог недоступен"
            description="Не удалось загрузить клубы. Попробуйте обновить список ещё раз."
            actionLabel="Повторить"
            onAction={() => void refetch()}
            role="alert"
          />
        ) : null}

        {!resolvedIsLoading && !resolvedError && clubs.length === 0 ? (
          hasSearch ? (
            <NeutralStatePanel
              title="Ничего не найдено"
              description={`По запросу «${normalizedSearch}» пока нет совпадений. Попробуйте другой запрос.`}
              actionLabel="Сбросить поиск"
              onAction={() => setSearchInput('')}
            />
          ) : (
            <NeutralStatePanel
              title="Каталог пока пуст"
              description="Когда список клубов появится, он отобразится на этом экране без дополнительного оформления."
            />
          )
        ) : null}

        {!resolvedIsLoading && !resolvedError && clubs.length > 0 ? (
          <>
            <ul className="grid grid-cols-2 gap-2.5 min-[390px]:gap-3 min-[768px]:grid-cols-3 min-[768px]:gap-4 min-[1024px]:grid-cols-4">
              {clubs.map((club) => (
                <CatalogCard
                  key={club.id}
                  club={club}
                  onOpen={(selectedClub) => {
                    trackEvent('catalog_club_click', {
                      clubId: selectedClub.id,
                      accessStatus: selectedClub.accessStatus,
                    });
                    trackYandexGoal('catalog_club_open', {
                      club_id: selectedClub.id,
                      source_surface: 'catalog',
                    });
                    void navigate(buildClubHref(selectedClub, 'catalog'));
                  }}
                />
              ))}
            </ul>

            {previewState === null && hasNextPage ? (
              <div
                ref={loadMoreRef}
                data-testid="catalog-load-more-sentinel"
                aria-hidden="true"
                className="h-px w-full"
              />
            ) : null}

            {previewState === null && isFetchingNextPage ? (
              <NeutralLoadingState
                rows={1}
                ariaLabel="Загрузка следующей порции клубов"
                className="pt-1"
              />
            ) : null}
          </>
        ) : null}
      </section>

      <footer className="space-y-1 px-1 pb-1 pt-0.5 min-[430px]:pb-1.5">
        <button
          type="button"
          onClick={handleLegalHubOpen}
          data-testid="catalog-page-legal-footer"
          className="inline-flex items-center gap-1 rounded-[12px] px-0.5 py-0.5 text-left text-[11px] font-medium leading-none text-[rgba(94,81,58,0.66)] transition-colors hover:text-[rgba(94,81,58,0.82)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(94,81,58,0.18)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(247,240,227,0.92)]"
        >
          <span>Inhound @ Юридическая информация</span>
        </button>
      </footer>
    </PageFrame>
  );
}
